/** @jsx jsx */
import React, { useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { N40, N0 } from '@atlaskit/theme/colors';
import { gridSize as getGridSize } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages';

const gridSize = getGridSize();

type Props = {
	isStoryPointsEnabled: boolean;
	isOriginalEstimationEnabled: boolean;
	isRemainingTimeEstimationEnabled: boolean;
	estimationFieldName: string;
	includeSubtasks: boolean;
};

const SummaryTableHeader = ({
	isStoryPointsEnabled,
	isOriginalEstimationEnabled,
	isRemainingTimeEstimationEnabled,
	estimationFieldName,
	includeSubtasks,
}: Props) => {
	const { formatMessage } = useIntl();

	const formattedEstimationFieldName = useMemo(
		() =>
			estimationFieldName
				? estimationFieldName[0].toUpperCase() + estimationFieldName.slice(1).toLowerCase()
				: '',
		[estimationFieldName],
	);

	return (
		<thead css={styles}>
			<tr>
				<th css={bottomBorderStyles}>
					<Heading level="h200">{formatMessage(messages.assigneeColumnHeader)}</Heading>
				</th>
				<th css={bottomBorderStyles}>
					<Heading level="h200">{formatMessage(messages.issueCountColumnHeader)}</Heading>
				</th>
				{includeSubtasks && (
					<th css={bottomBorderStyles}>
						<Heading level="h200">{formatMessage(messages.subtasksColumnHeader)}</Heading>
					</th>
				)}
				{isStoryPointsEnabled && (
					<th css={bottomBorderStyles}>
						<Heading level="h200">{formattedEstimationFieldName}</Heading>
					</th>
				)}
				{isOriginalEstimationEnabled && (
					<th css={bottomBorderStyles}>
						<Heading level="h200">{formattedEstimationFieldName}</Heading>
					</th>
				)}
				{isRemainingTimeEstimationEnabled && (
					<th css={bottomBorderStyles}>
						<Heading level="h200">{formatMessage(messages.remainingEstimateColumnHeader)}</Heading>
					</th>
				)}
			</tr>
		</thead>
	);
};

export default SummaryTableHeader;
const styles = css({
	position: 'sticky',
	top: 0,
	zIndex: 10,
	width: '100%',
	backgroundColor: `${token('elevation.surface.overlay', N0)}`,
});

const bottomBorderStyles = css({
	paddingBottom: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderBottom: `${gridSize / 4}px solid ${token('color.border', N40)}`,
});
